import { HeadsetMic } from "@material-ui/icons";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import loginService from "../../../domain/services/login.service";
import announcementService from "../../../domain/services/marketing/announcement.service";
import { announcements } from "../../../domain/constant/announcements.constant";
import supportChatEmbedService from "../../../domain/services/support.chat.embed.service";
import { appConstants } from "../../../domain/constant/app.constant";

const LAST_ANONYMOUS_TEMPORARY_ID_KEY = '@facilita-app/support-chat/last-anonymous-temporary-id';

const getIframeOrigin = (url) => {
    try {
        return new URL(url).origin;
    } catch (_error) {
        return null;
    }
}

const getLastAnonymousTemporaryId = () =>
    localStorage.getItem(LAST_ANONYMOUS_TEMPORARY_ID_KEY);

const saveLastAnonymousTemporaryId = (temporaryId) => {
    if (temporaryId)
        localStorage.setItem(LAST_ANONYMOUS_TEMPORARY_ID_KEY, `${temporaryId}`);
}

export default function SupportChatWidget({ forceAuthenticated = false }) {

    const isAuthenticated = forceAuthenticated || loginService.isLogin();
    const mode = isAuthenticated ? 'authenticated' : 'anonymous';
    const iframeRef = useRef(null);
    const sessionRequestRef = useRef({ key: null, promise: null });
    const verificationRequestRef = useRef({ key: null, promise: null });

    const checkShowHelperText = () => announcementService.isShowed(announcements.supportChatHelperText);

    const [open, setOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const [showHelperText, setShowHelperText] = useState(false);
    const [iframeHeight, setIframeHeight] = useState(null);
    const [mounted, setMounted] = useState(false);

    const iframeSrc = useMemo(() => {
        const params = new URLSearchParams({
            clientId: supportChatEmbedService.CLIENT_ID,
            mode,
            parentOrigin: window.location.origin
        });

        return `${appConstants.orbitaSupportChatUrl}?${params.toString()}`;
    }, [mode]);

    const iframeOrigin = useMemo(() => getIframeOrigin(iframeSrc), [iframeSrc]);

    const postToIframe = (type, payload = {}) => {
        if (!iframeRef.current?.contentWindow || !iframeOrigin)
            return;

        iframeRef.current.contentWindow.postMessage({
            source: 'facilita-support-chat',
            type,
            payload
        }, iframeOrigin);
    }

    const sendVisibilityToIframe = (isOpen = open) =>
        postToIframe('facilita:visibility', { open: isOpen });

    const sendSessionToIframe = (payload) =>
        postToIframe('facilita:session', payload);

    const sendAnonymousVerificationToIframe = (payload) =>
        postToIframe('facilita:anonymous-verification', payload);

    const createSession = async (payload = {}) => {
        const requestedMode = isAuthenticated ? 'authenticated' : payload.mode;
        const temporaryId = requestedMode === 'authenticated'
            ? (payload.temporaryId || getLastAnonymousTemporaryId())
            : payload.temporaryId;
        const requestKey = JSON.stringify({
            mode: requestedMode,
            temporaryId,
            verificationId: payload.verificationId,
            code: payload.code,
            deviceToken: payload.deviceToken
        });

        if (sessionRequestRef.current.key === requestKey && sessionRequestRef.current.promise)
            return sessionRequestRef.current.promise;

        const sessionRequest = {
            clientId: supportChatEmbedService.CLIENT_ID,
            temporaryId
        };

        const requestPromise = (requestedMode === 'anonymous'
            ? supportChatEmbedService.createAnonymousSession({
                ...sessionRequest,
                name: payload.name,
                email: payload.email,
                phone: payload.phone,
                verificationId: payload.verificationId,
                code: payload.code,
                deviceToken: payload.deviceToken
            })
            : supportChatEmbedService.createAuthenticatedSession(sessionRequest))
            .then(session => {
                if (session.mode === 'anonymous' || requestedMode === 'anonymous')
                    saveLastAnonymousTemporaryId(session.temporaryId || temporaryId);

                sendSessionToIframe({ ...session, mode: session.mode || requestedMode });
                return session;
            })
            .finally(() => {
                if (sessionRequestRef.current.key === requestKey)
                    sessionRequestRef.current = { key: null, promise: null };
            });

        sessionRequestRef.current = { key: requestKey, promise: requestPromise };

        return requestPromise;
    }

    const createAnonymousVerification = async (payload = {}) => {
        const requestKey = JSON.stringify({
            temporaryId: payload.temporaryId,
            name: payload.name,
            email: payload.email,
            phone: payload.phone
        });

        if (verificationRequestRef.current.key === requestKey && verificationRequestRef.current.promise)
            return verificationRequestRef.current.promise;

        const requestPromise = supportChatEmbedService.createAnonymousVerification({
            clientId: supportChatEmbedService.CLIENT_ID,
            temporaryId: payload.temporaryId,
            name: payload.name,
            email: payload.email,
            phone: payload.phone
        })
            .then(verification => {
                saveLastAnonymousTemporaryId(verification.temporaryId || payload.temporaryId);
                sendAnonymousVerificationToIframe(verification);
                return verification;
            })
            .finally(() => {
                if (verificationRequestRef.current.key === requestKey)
                    verificationRequestRef.current = { key: null, promise: null };
            });

        verificationRequestRef.current = { key: requestKey, promise: requestPromise };

        return requestPromise;
    }

    const handleIframeMessage = (event) => {
        if (!iframeOrigin || event.origin !== iframeOrigin)
            return;

        const data = event.data || {};

        if (data.source !== 'orbita-support-chat')
            return;

        const sendSessionErrorToIframe = (error) => {
            postToIframe('facilita:session-error', {
                message: error?.response?.data?.message || 'session-failed'
            });
        };

        const actions = {
            'orbita:ready': () => {
                saveLastAnonymousTemporaryId(data.payload?.temporaryId);

                if (isAuthenticated && data.payload?.mode === 'anonymous') {
                    postToIframe('facilita:authenticated-context');
                    createSession({ ...data.payload, mode: 'authenticated' }).catch(sendSessionErrorToIframe);
                }

                sendVisibilityToIframe();
            },
            'orbita:anonymous-verification-request': () => {
                if (isAuthenticated) {
                    postToIframe('facilita:authenticated-context');
                    createSession({ ...data.payload, mode: 'authenticated' }).catch(sendSessionErrorToIframe);
                    return;
                }

                createAnonymousVerification(data.payload).catch((error) => {
                    postToIframe('facilita:anonymous-verification-error', {
                        message: error?.response?.data?.message || 'verification-failed',
                        resendAvailableAt: error?.response?.data?.resendAvailableAt,
                        verificationId: error?.response?.data?.verificationId,
                        temporaryId: error?.response?.data?.temporaryId,
                        expiresAt: error?.response?.data?.expiresAt
                    });
                });
            },
            'orbita:session-request': () => createSession(data.payload).catch(sendSessionErrorToIframe),
            'orbita:resize': () => setIframeHeight(data.payload?.height || null),
            'orbita:unread-count': () => setUnreadCount(data.payload?.count || 0),
            'orbita:close': () => handleClose(),
            'orbita:error': () => null
        };

        if (actions[data.type])
            actions[data.type]();
    }

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        if (announcementService.isShowed(announcements.supportChatHelperText)) return;
        setShowHelperText(true);
    }, []);

    useEffect(() => {
        window.addEventListener('message', handleIframeMessage);

        return () => window.removeEventListener('message', handleIframeMessage);
    }, [iframeOrigin, open, isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (!open || checkShowHelperText() || !isAuthenticated)
            return;

        announcementService.markViewed(announcements.supportChatHelperText);
        setShowHelperText(false);
    }, [open, isAuthenticated]);

    const handleOpen = () => {
        setOpen(true);
        sendVisibilityToIframe(true);
    }

    const handleClose = () => {
        setOpen(false);
        sendVisibilityToIframe(false);
    }

    const widget = (
        <div className="font-['SF_Pro']">
            <div
                className={`
                    fixed h-12 w-12 lg:h-14 lg:w-14 right-8 rounded-full bg-primary hover:brightness-105 cursor-pointer transition-[filter_.3s_ease] z-[100000]
                    ${open ? '-bottom-12 lg:-bottom-14' : 'bottom-8'}
                `}
                style={{
                    boxShadow: '0 0 8px #0002',
                    pointerEvents: open ? 'none' : 'auto',
                    transition: 'bottom .3s ease-in-out',
                    zIndex: 2147483647,
                }}
                onClick={handleOpen}
            >
                <div className="relative w-full h-full flex justify-center items-center">

                    {showHelperText && (
                        <div className="absolute right-[calc(100%+8px)] top-1/2 -translate-y-1/2">
                            <div className="text-black bg-white px-3 py-2 rounded-lg text-nowrap font-medium shadow border border-[#ededed]">
                                Precisa de ajuda?
                            </div>
                        </div>
                    )}

                    {!!unreadCount && (
                        <div className="absolute top-0 right-0 size-5 bg-red-500 rounded-full text-white text-xs font-semibold flex justify-center items-center">
                            {unreadCount}
                        </div>
                    )}

                    <HeadsetMic
                        className="!text-3xl sm:!text-[32px]"
                        style={{
                            color: '#fff',
                            transform: 'translate(1px, -1px)',

                        }}
                    />
                </div>
            </div>

            <div
                className={`
                    z-[100001]
                    fixed left-0 bottom-0 top-0 right-0
                    sm:right-8 sm:bottom-8 sm:top-auto sm:left-auto sm:w-full sm:max-w-md sm:h-[564px]
                `}
                style={{
                    pointerEvents: open ? 'auto' : 'none',
                    opacity: open ? 1 : 0,
                    transform: open ? 'translateY(0)' : 'translateY(10%)',
                    transition: 'opacity .3s ease-in-out, transform 0.3s ease-in-out',
                    height: iframeHeight && window.innerWidth >= 640 ? Math.min(iframeHeight, 640) : undefined,
                    zIndex: 2147483647,
                }}
            >
                <iframe
                    key={iframeSrc}
                    allow="clipboard-read; clipboard-write"
                    className="w-full h-full bg-transparent border-0 sm:rounded-xl"
                    ref={iframeRef}
                    src={iframeSrc}
                    title="Atendimento Facilita"
                />
            </div>
        </div>
    )

    if (!mounted)
        return null;

    return createPortal(widget, document.body);

}
